import { AlertTriangle, ExternalLink, FilePlus2, Undo2 } from 'lucide-react'

import { StatusPill } from '@/components/status-pill'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { AmountList } from '@/features/projects/amount-list'
import { cn, formatMoney, parseUTCDate } from '@/lib/utils'
import { NewInvoiceDialog } from '@/features/receivables/new-invoice-dialog'
import { RefundDialog } from '@/features/receivables/refund-dialog'
import type { ReactNode } from 'react'

import { paymentDetail } from './payments'

import {
  invoiceStatusLabel,
  invoiceStatusTone,
  type ProjectPayments,
  useProjectPayments,
} from './project-payments'

const shortDate = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
const formatDay = (value: string) => shortDate.format(parseUTCDate(value))

const ENTRY_LABELS: Record<string, string> = {
  payment: 'Payment received',
  payment_out_of_band: 'Payment recorded',
  external_payment: 'Payment recorded',
  payment_processing: 'Bank payment processing',
  payment_failed: 'Payment failed',
  refund: 'Refunded',
  credit: 'Taken off an invoice',
}

/**
 * What the project's customer has been invoiced and has paid: the Customer half of the project's
 * Payments card, with New invoice (and Refund once something was paid).
 */
export function ProjectCustomerPayments({ projectUuid }: { projectUuid: string }) {
  const { data, isLoading, isError } = useProjectPayments(projectUuid)
  // Staff can cut an invoice on any project, as often as needed (USA-378).
  const newInvoice = (
    <NewInvoiceDialog source={{ projectUuid }} trigger={smallTrigger(<FilePlus2 aria-hidden="true" />, 'New invoice')} />
  )
  const recorded = data?.entries.some((entry) => entry.entry_type === 'external_payment')
  const empty = !data || (data.invoices.length === 0 && (data.prepaid?.length ?? 0) === 0 && !recorded)
  return (
    <section aria-labelledby={`customer-payments-${projectUuid}`} className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h4 id={`customer-payments-${projectUuid}`} className="text-xs font-medium text-muted-foreground">
          Customer
        </h4>
        {!isLoading && !isError && (
          <div className="flex items-center gap-1.5">
            {newInvoice}
            {!empty && <RefundDialog source={{ projectUuid }} trigger={smallTrigger(<Undo2 aria-hidden="true" />, 'Refund')} />}
          </div>
        )}
      </div>
      {isLoading ? (
        <Skeleton className="h-12 w-full" />
      ) : isError ? (
        <p className="text-sm text-destructive">Customer payments could not be loaded. Reload the page to try again.</p>
      ) : empty || !data ? (
        <p className="text-sm text-muted-foreground">No invoices yet</p>
      ) : (
        <ProjectPaymentsSummary payments={data} />
      )}
    </section>
  )
}

function smallTrigger(icon: ReactNode, label: string) {
  return (
    <Button size="xs" variant="outline">
      {icon}
      {label}
    </Button>
  )
}

export function ProjectPaymentsSummary({ payments }: { payments: ProjectPayments }) {
  const owed = Number(payments.owed)
  const open = Number(payments.invoiced_open)
  const notInvoiced = Number(payments.not_yet_invoiced)
  const history = payments.entries.filter((entry) => entry.entry_type in ENTRY_LABELS)
  return (
    <div className="space-y-3">
      <AmountList
        rows={[
          { label: 'Paid', amount: payments.paid, strong: true },
          { label: 'Owed', amount: payments.owed, strong: true, muted: owed <= 0 },
          { label: 'Quote total', amount: payments.quote_total },
          ...(open > 0 && notInvoiced > 0 ? [{ label: 'Invoiced, unpaid', amount: open }] : []),
          ...(notInvoiced > 0 ? [{ label: 'Not invoiced yet', amount: notInvoiced }] : []),
        ]}
      />

      {payments.prepaid?.length ? (
        <section aria-labelledby={`prepaid-${payments.project_id}`} className="space-y-1">
          <h5 id={`prepaid-${payments.project_id}`} className="text-xs font-medium text-muted-foreground">
            Paid separately
          </h5>
          <ul className="space-y-0.5">
            {payments.prepaid.map((item) => (
              <li key={item.quote.id} className="py-1 text-sm">
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate font-medium">{item.quote.quote_number}</span>
                  <span className="shrink-0 font-medium tabular-nums">{formatMoney(item.amount)}</span>
                </div>
                <div className="mt-1 flex items-center justify-between gap-2 text-xs text-muted-foreground">
                  <span className="min-w-0 truncate">
                    Prepaid{item.reference ? ` · Ref ${item.reference}` : ''}
                  </span>
                  <StatusPill tone="success">Paid</StatusPill>
                </div>
                {item.note ? <p className="mt-1 whitespace-pre-line text-xs text-muted-foreground">{item.note}</p> : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {payments.invoices.length > 0 ? (
        <section aria-labelledby={`invoices-${payments.project_id}`} className="space-y-1">
          <h5 id={`invoices-${payments.project_id}`} className="text-xs font-medium text-muted-foreground">
            Invoices
          </h5>
          <ul className="space-y-0.5">
            {payments.invoices.map((invoice) => (
              <li key={invoice.id} className="py-1 text-sm">
                <div className="flex items-center justify-between gap-2">
                  {invoice.pay_url ? (
                    // eslint-disable-next-line no-restricted-syntax -- the customer's invoice page
                    <a
                      href={invoice.pay_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex min-w-0 items-center gap-1 font-medium text-primary hover:underline"
                    >
                      <span className="truncate">{invoice.number ?? invoice.quote?.quote_number ?? 'Invoice'}</span>
                      <ExternalLink className="h-3 w-3 shrink-0" aria-hidden="true" />
                      <span className="sr-only">(opens the invoice)</span>
                    </a>
                  ) : (
                    <span className="truncate font-medium">{invoice.quote?.quote_number ?? 'Invoice'}</span>
                  )}
                  <span className="shrink-0 font-medium tabular-nums">{formatMoney(invoice.amount_due)}</span>
                </div>
                <div className="mt-1 flex items-center justify-between gap-2 text-xs text-muted-foreground">
                  <span className="min-w-0 truncate">
                    {invoice.kind === 'additional' ? (invoice.snapshot.items[0]?.description ?? 'Additional invoice') : invoice.quote?.quote_number}
                    {invoice.paid_at
                      ? ` · Paid ${formatDay(invoice.paid_at)}`
                      : invoice.due_at && invoice.status === 'open'
                        ? ` · Due ${formatDay(invoice.due_at)}`
                        : ''}
                  </span>
                  <StatusPill tone={invoiceStatusTone(invoice)}>{invoiceStatusLabel(invoice)}</StatusPill>
                </div>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {history.length > 0 && (
        <section aria-labelledby={`payments-${payments.project_id}`} className="space-y-1">
          <h5 id={`payments-${payments.project_id}`} className="text-xs font-medium text-muted-foreground">
            Payment history
          </h5>
          <ul>
            {history.map((entry) => (
              <li key={entry.id} className="py-1 text-xs">
                <div className="flex items-center justify-between gap-2">
                  <span className="flex min-w-0 items-center gap-1.5">
                    {entry.entry_type === 'payment_failed' && (
                      <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-destructive" aria-hidden="true" />
                    )}
                    <span className={entry.entry_type === 'payment_failed' ? 'text-destructive' : undefined}>
                      {entry.removed_at ? 'Payment removed' : ENTRY_LABELS[entry.entry_type]}
                    </span>
                    <span className="text-muted-foreground">{formatDay(entry.occurred_at)}</span>
                  </span>
                  {entry.entry_type !== 'payment_failed' && (
                    <span className={cn('shrink-0 font-medium tabular-nums', entry.removed_at && 'text-muted-foreground line-through')}>
                      {entry.entry_type === 'refund' || entry.entry_type === 'credit' ? `−${formatMoney(entry.amount)}` : formatMoney(entry.amount)}
                    </span>
                  )}
                </div>
                {entry.recorded_by ? (
                  <p className="mt-0.5 text-muted-foreground">
                    {paymentDetail(entry)}
                    {entry.removed_by ? ` · Removed by ${entry.removed_by.name}` : ''}
                  </p>
                ) : null}
                {entry.note ? <p className="mt-0.5 whitespace-pre-line text-muted-foreground">{entry.note}</p> : null}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  )
}
